import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { listClientCourses, getClientCommunityOptions } from "../../api/clientCoursesApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import {
  ClientPortalError,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import ClientContentPublishBadges from "../../components/clientPortal/ClientContentPublishBadges";
import "../../styles/clientCoursesList.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "HIDDEN", label: "Hidden" },
  { value: "ARCHIVED", label: "Archived" },
];

const STATUS_BADGE_CLASS = {
  ACTIVE: "ccol-card__badge--active",
  DRAFT: "ccol-card__badge--draft",
  HIDDEN: "ccol-card__badge--hidden",
  ARCHIVED: "ccol-card__badge--archived",
};

function SkeletonGrid() {
  return (
    <div className="ccol-skeleton-grid">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="ccol-skeleton-card">
          <div className="ccol-skeleton-card__hero" />
          <div className="ccol-skeleton-card__body">
            <div className="ccol-skeleton-bar ccol-skeleton-bar--title" />
            <div className="ccol-skeleton-bar ccol-skeleton-bar--text" />
            <div className="ccol-skeleton-bar ccol-skeleton-bar--text-long" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryMetrics({ summary }) {
  if (!summary) return null;

  return (
    <div className="ccol-metrics">
      <div className="ccol-metric">
        <span className="ccol-metric__label">Total</span>
        <span className="ccol-metric__value">{summary.total ?? 0}</span>
      </div>
      <div className="ccol-metric ccol-metric--active">
        <span className="ccol-metric__label">Active</span>
        <span className="ccol-metric__value">{summary.active ?? 0}</span>
      </div>
      <div className="ccol-metric ccol-metric--draft">
        <span className="ccol-metric__label">Draft</span>
        <span className="ccol-metric__value">{summary.draft ?? 0}</span>
      </div>
      <div className="ccol-metric ccol-metric--featured">
        <span className="ccol-metric__label">Featured</span>
        <span className="ccol-metric__value">{summary.featured ?? 0}</span>
      </div>
    </div>
  );
}

function formatLocation(course) {
  const parts = [course.city, course.stateRegion].filter(Boolean);
  if (!parts.length) return "";
  return parts.join(", ");
}

function CourseCard({ course, clientViewId }) {
  const paths = useClientPortalPaths();
  const location = formatLocation(course);
  const statusClass = STATUS_BADGE_CLASS[course.displayStatus] || "ccol-card__badge--draft";

  return (
    <article className="ccol-card">
      <div className="ccol-card__hero">
        {course.heroImageUrl ? (
          <img src={course.heroImageUrl} alt={course.courseName} loading="lazy" />
        ) : (
          <span className="ccol-card__hero-placeholder" aria-hidden="true">&#9971;</span>
        )}
        <div className="ccol-card__badges">
          <span className={`ccol-card__badge ${statusClass}`}>
            {formatStatusLabel(course.displayStatus)}
          </span>
          {course.isFeatured && (
            <span className="ccol-card__badge ccol-card__badge--featured">Featured</span>
          )}
        </div>
      </div>

      <div className="ccol-card__body">
        <h3 className="ccol-card__name">{course.courseName}</h3>

        {location && (
          <div className="ccol-card__location">
            <span className="ccol-card__location-icon" aria-hidden="true">&#128205;</span>
            {location}
            {course.publicPrivateType ? ` \u00B7 ${course.publicPrivateType}` : ""}
          </div>
        )}

        {course.shortDescription && (
          <p className="ccol-card__description">{course.shortDescription}</p>
        )}

        <div className="ccol-card__meta">
          {course.communityName && (
            <span className="ccol-card__meta-chip ccol-card__meta-chip--community">
              {course.communityName}
            </span>
          )}
          {course.numberOfHoles && (
            <span className="ccol-card__meta-chip">{course.numberOfHoles} holes</span>
          )}
          {course.par && (
            <span className="ccol-card__meta-chip">Par {course.par}</span>
          )}
          {course.courseType && (
            <span className="ccol-card__meta-chip">{course.courseType}</span>
          )}
        </div>

        <div className="ccol-card__publish-row">
          <ClientContentPublishBadges item={course} />
        </div>

        <div className="ccol-card__stats">
          {course.opportunityCount != null && (
            <span className="ccol-card__stat">
              <span className="ccol-card__stat-icon" aria-hidden="true">&#128200;</span>
              <strong>{course.opportunityCount}</strong> leads
            </span>
          )}
          <span className="ccol-card__stat">
            <span className="ccol-card__stat-icon" aria-hidden="true">&#128337;</span>
            Updated {formatClientDate(course.updatedAt)}
          </span>
        </div>
      </div>

      <div className="ccol-card__actions">
        <Link
          to={paths.viewSection(clientViewId, "courses", course.courseId)}
          className="ccol-card__action ccol-card__action--primary"
        >
          View Details
        </Link>
        {course.canManage && (
          <Link
            to={paths.viewSection(clientViewId, "courses", course.courseId, "edit")}
            className="ccol-card__action ccol-card__action--secondary"
          >
            Edit
          </Link>
        )}
      </div>
    </article>
  );
}

const ClientCoursesListPage = () => {
  const { clientViewId } = useParams();
  const [viewName, setViewName] = useState("");
  const [data, setData] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [communityId, setCommunityId] = useState("");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);
  const paths = useClientPortalPaths();

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, listBody, optionsBody] = await Promise.all([
        getClientPortalView(clientViewId),
        listClientCourses(clientViewId, {
          page,
          pageSize: 25,
          search: search || undefined,
          status: status || undefined,
          featured: featuredOnly || undefined,
          communityId: communityId || undefined,
          sort,
        }),
        getClientCommunityOptions(clientViewId).catch(() => ({ communities: [] })),
      ]);
      setViewName(viewBody.clientView?.name || "Client View");
      setData(listBody);
      setCommunities(optionsBody.communities || []);
    } catch (error) {
      setErrorMessage(error.message || "We could not load courses. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, page, search, status, featuredOnly, communityId, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary;

  return (
    <div className="ccol">
      <div className="ccol-header">
        <div className="ccol-header__left">
          <nav className="ccol-breadcrumb" aria-label="Breadcrumb">
            <Link to={paths.view(clientViewId)}>{viewName || "Client View"}</Link>
            <span className="ccol-breadcrumb__sep" aria-hidden="true">/</span>
            <span>Courses</span>
          </nav>
          <h1 className="ccol-header__title">Courses</h1>
          <p className="ccol-header__subtitle">
            Manage golf course content for your site
          </p>
        </div>
        {data?.canManage && (
          <div className="ccol-header__actions">
            <Link
              to={paths.viewSection(clientViewId, "courses", "new")}
              className="ccf-btn ccf-btn--primary"
            >
              + Add Course
            </Link>
          </div>
        )}
      </div>

      {errorMessage && <ClientPortalError message={errorMessage} />}

      {loading && !data ? (
        <SkeletonGrid />
      ) : data ? (
        <>
          <SummaryMetrics summary={summary} />

          <div className="ccol-toolbar">
            <div className="ccol-search">
              <span className="ccol-search__icon" aria-hidden="true">&#128269;</span>
              <input
                type="search"
                className="ccol-search__input"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search by name, city, or designer..."
              />
            </div>

            <span className="ccol-toolbar__divider" aria-hidden="true" />

            <select
              className="ccol-filter-select"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {communities.length > 0 && (
              <select
                className="ccol-filter-select"
                value={communityId}
                onChange={(e) => {
                  setPage(1);
                  setCommunityId(e.target.value);
                }}
              >
                <option value="">All communities</option>
                {communities.map((c) => (
                  <option key={c.communityId} value={c.communityId}>
                    {c.communityName}
                    {c.city ? ` \u2014 ${c.city}` : ""}
                  </option>
                ))}
              </select>
            )}

            <select
              className="ccol-filter-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="updated">Recently updated</option>
              <option value="name">Name A-Z</option>
              <option value="featured">Featured first</option>
              <option value="status">Status</option>
              <option value="community">Community</option>
              <option value="sort_order">Sort order</option>
            </select>

            <button
              type="button"
              className={`ccol-toggle-chip ${featuredOnly ? "ccol-toggle-chip--active" : ""}`}
              onClick={() => {
                setPage(1);
                setFeaturedOnly((v) => !v);
              }}
            >
              {featuredOnly ? "\u2605" : "\u2606"} Featured only
            </button>
          </div>

          {!loading && data.courses?.length === 0 ? (
            <div className="ccol-empty">
              <span className="ccol-empty__icon" aria-hidden="true">&#9971;</span>
              <h2 className="ccol-empty__title">No courses yet</h2>
              <p className="ccol-empty__text">
                No courses match your current filters. Try adjusting your search or add a new course to get started.
              </p>
            </div>
          ) : (
            <div className="ccol-grid">
              {data.courses?.map((course) => (
                <CourseCard
                  key={course.courseId}
                  course={course}
                  clientViewId={clientViewId}
                />
              ))}
            </div>
          )}

          {data.pagination?.totalPages > 1 && (
            <div className="ccol-pagination">
              <button
                type="button"
                className="ccol-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                &larr; Previous
              </button>
              <span className="ccol-pagination__info">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>
              <button
                type="button"
                className="ccol-pagination__btn"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next &rarr;
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default ClientCoursesListPage;
